import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOSchema from '@/components/base/SEOSchema';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, testimonials } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <p className="text-foreground-500">漫画未找到</p>
        </div>
        <Footer />
      </>
    );
  }

  const comicTitle = `${comic.title} - 无删减韩国漫画`;
  const comicDesc = `${comic.title}，${comic.author}作品。${comic.description.substring(0, 100)}... 动漫之家提供${comic.title}无删减下拉式免费阅读，画质高清加载流畅。`;

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: comicTitle,
      description: comicDesc,
      author: { '@type': 'Person', name: comic.author },
      datePublished: '2026-01-15',
      dateModified: comic.updatedAt,
      publisher: {
        '@type': 'Organization',
        name: '动漫之家',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://hgok7.com/' },
        { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://hgok7.com/category/romance' },
        { '@type': 'ListItem', position: 3, name: comic.categoryName, item: `http://hgok7.com/category/${comic.category}` },
        { '@type': 'ListItem', position: 4, name: comic.title },
      ],
    },
  ];

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 3);

  return (
    <>
      <SEOSchema
        schemas={articleSchema}
        canonicalUrl={`http://hgok7.com/comic/${slug}`}
        pageTitle={comicTitle}
        pageDescription={comicDesc}
        pageKeywords={`${comic.title},韩国漫画,无删减漫画,下拉式漫画,韩漫在线免费阅读,${comic.categoryName},动漫之家`}
        ogTitle={comicTitle}
        ogDescription={comicDesc}
        ogImage={comic.coverImage}
        ogType="article"
      />

      <Navbar />

      <main>
        {/* Comic Info Section */}
        <article className="pt-24 pb-16 md:pb-24 px-6 md:px-14 lg:px-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '漫画分类', href: '/category/romance' },
                  { label: comic.categoryName, href: `/category/${comic.category}` },
                  { label: comic.title },
                ]}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:gap-0" style={{ width: '100%' }}>
              {/* Left - Cover */}
              <div className="lg:w-[5fr]">
                <div className="w-full max-w-sm mx-auto lg:mx-0 aspect-[3/4] rounded-3xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 无删减韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Right - Info */}
              <div className="lg:w-[5fr] lg:pl-12 flex flex-col justify-center">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-label font-semibold ${
                    comic.status === '连载中'
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-accent-500 text-background-50'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-label font-semibold">
                    无删减
                  </span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {comic.title}
                </h1>

                <p className="text-sm text-foreground-500 mb-6">
                  {comic.author} · {comic.categoryName}
                </p>

                <p className="text-sm text-foreground-600 leading-relaxed mb-8 max-w-lg">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-label font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <p className="text-2xl font-heading font-bold text-foreground-950">{comic.rating}</p>
                    <p className="text-xs text-foreground-400 mt-1">评分</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <p className="text-2xl font-heading font-bold text-foreground-950">{(comic.views / 10000).toFixed(0)}万</p>
                    <p className="text-xs text-foreground-400 mt-1">阅读量</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <p className="text-2xl font-heading font-bold text-foreground-950">{comic.chapters}</p>
                    <p className="text-xs text-foreground-400 mt-1">章节数</p>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <p className="text-2xl font-heading font-bold text-foreground-950">{comic.updatedAt.slice(5)}</p>
                    <p className="text-xs text-foreground-400 mt-1">最后更新</p>
                  </div>
                </div>

                <Link
                  to="/category/romance"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-semibold hover:bg-foreground-800 transition-all duration-200 cursor-pointer w-fit"
                >
                  立即阅读
                  <i className="ri-arrow-right-up-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Reviews Section */}
        <section className="bg-background-50 py-16 md:py-24 px-6 md:px-14 lg:px-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-0" style={{ width: '100%' }}>
              <div className="lg:w-[6fr] pr-0 lg:pr-16">
                <div className="flex items-center gap-2 mb-8">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                  <h2 className="text-xs font-label font-semibold text-foreground-500 tracking-widest uppercase">
                    精选评论
                  </h2>
                </div>

                {testimonials.map((t, idx) => (
                  <blockquote
                    key={t.id}
                    className={`${idx > 0 ? 'mt-10 pt-10 border-t border-background-200' : ''}`}
                  >
                    <p className="text-xl md:text-2xl font-heading font-medium text-foreground-900 leading-relaxed italic">
                      {t.content}
                    </p>
                    <footer className="mt-5 flex items-center gap-3">
                      <span className="text-sm font-label font-bold text-foreground-950">{t.name}</span>
                      <span className="text-xs text-foreground-400">{t.role}</span>
                      <span className="flex items-center gap-0.5 ml-2">
                        {[...Array(t.rating)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-sm w-3.5 h-3.5 flex items-center justify-center"></i>
                        ))}
                      </span>
                    </footer>
                  </blockquote>
                ))}
              </div>

              <div className="lg:w-[4fr] flex items-center justify-center">
                <div className="w-full h-[350px] rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20person%20reading%20webtoon%20on%20tablet%20in%20cozy%20evening%20setting%2C%20warm%20lamp%20light%2C%20comfortable%20couch%2C%20immersed%20expression%2C%20lifestyle%20photography%2C%20minimal%20composition%2C%20warm%20amber%20tones%2C%20relaxing%20atmosphere&width=600&height=700&seq=review-side-001&orientation=portrait"
                    alt="读者阅读体验"
                    title="韩漫在线免费阅读"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-100 py-16 md:py-24 px-6 md:px-14 lg:px-20">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-10">
                同类推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/comic/${rc.slug}`}
                    className="group block bg-background-50 rounded-2xl overflow-hidden hover:bg-background-50/80 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={rc.coverImage}
                        alt={rc.title}
                        title={`${rc.title} - 韩国漫画推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-heading font-bold text-foreground-950">{rc.title}</h3>
                      <p className="text-xs text-foreground-400 mt-1">{rc.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section
          className="relative h-[60vh] w-full overflow-hidden"
          aria-label="开始阅读引导"
        >
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20reading%20atmosphere%20with%20digital%20tablet%20displaying%20colorful%20webtoon%20art%2C%20warm%20ambient%20lighting%2C%20comfortable%20evening%20setting%2C%20hot%20beverage%20on%20side%20table%2C%20relaxing%20home%20interior%2C%20golden%20warm%20tones%2C%20inviting%20mood&width=1920&height=800&seq=cta-detail-001&orientation=landscape"
              alt="开始阅读韩国漫画"
              title="开始阅读韩漫在线免费"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          </div>

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white mb-4">
              立即开始阅读
            </h2>
            <p className="text-sm md:text-base text-white/70 font-body max-w-md leading-relaxed mb-8">
              最新章节：{comic.latestChapter}
            </p>
            <Link
              to="/category/romance"
              className="whitespace-nowrap inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-label font-semibold hover:bg-foreground-800 transition-all duration-200 cursor-pointer"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500">
                <i className="ri-book-open-line text-background-50 text-xs"></i>
              </span>
              开始阅读
              <i className="ri-arrow-right-up-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}